import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ContactNoteWrapperWC } from '@juliusbaer-gcrm/pcc-contact-note-components';
import { JBEnvironmentId } from '@bjb-gcrm/gcrm-frontend-components';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { ZfBanner, ZfStack } from '@zafire/react';
import { getNestedRemoteUrl } from '../../config/module-federation-config';
import { LinkedContactNote, LinkedContactNotesGrid } from './LinkedContactNotesGrid';
import { ZfDividerTitle } from '../../components/common/ZfDividerTitle';

interface Props {
  newlyLinkedNotes: LinkedContactNote[];
  onLinkedNotesChange: (update: (current: LinkedContactNote[]) => LinkedContactNote[]) => void;
  brUuid?: string;
  existingNoteIds?: string[];
  addTopMargin?: boolean;
  showContactNoteRequired?: boolean;
  requiredAlertText?: string;
}

const CONTACT_NOTE_STUB_ENVS = ['LOCAL', 'CI', 'CI_PREVIEW'];
const NO_NOTE_IDS: string[] = [];

/**
 * Renders the contact-note module-federation remote (`ContactNoteWrapperWC`) for a given
 * business relation.
 *
 * In LOCAL and CI/CI_PREVIEW this loads a stub remote from pco-product-service-remote instead of
 * the real contact-note deployment — see `CONTACT_NOTE_STUB_ENVS`.
 *
 * @param brUuid - the business relation UID to load contact notes for.
 * @param newlyLinkedNotes - the notes linked in this session, listed in the grid below the button.
 * @param existingNoteIds - IDs of the notes the product already has. The grid shows only what is
 * being linked right now, so without these the remote would offer a note that is already on the
 * product and let it be linked a second time.
 */
const LinkContactNotesButton: React.FC<Props> = ({
  brUuid,
  newlyLinkedNotes,
  onLinkedNotesChange,
  existingNoteIds = NO_NOTE_IDS,
  addTopMargin = false,
  showContactNoteRequired = false,
  requiredAlertText,
}) => {
  const { t } = useTranslation();
  const env = useSelector((state: RootState) => state.appConfig.env);
  const isEnvironmentResolved = env !== 'UNKNOWN';
  const urlOverride = CONTACT_NOTE_STUB_ENVS.includes(env) ? getNestedRemoteUrl() : undefined;

  const callbackFunc = React.useCallback(
    (action: { type: string; payload: unknown[] }) => {
      const incoming = (action.payload as LinkedContactNote[]) ?? [];
      onLinkedNotesChange((current) => {
        const takenIds = new Set([...existingNoteIds, ...current.map((note) => note.contactNoteId)]);
        const added = incoming.filter((note) => note?.contactNoteId && !takenIds.has(note.contactNoteId));
        return added.length ? [...current, ...added] : current;
      });
    },
    [existingNoteIds, onLinkedNotesChange],
  );

  const handleUnlink = React.useCallback(
    (contactNoteId: string) => {
      onLinkedNotesChange((current) => current.filter((note) => note.contactNoteId !== contactNoteId));
    },
    [onLinkedNotesChange],
  );

  const linkedNoteIds = React.useMemo(
    () => [...new Set([...existingNoteIds, ...newlyLinkedNotes.map((note) => note.contactNoteId)])],
    [existingNoteIds, newlyLinkedNotes],
  );

  const linkButtonRef = React.useRef<HTMLDivElement>(null);
  const previousNoteCount = React.useRef(newlyLinkedNotes.length);

  React.useEffect(() => {
    const newNotesAdded = newlyLinkedNotes.length > previousNoteCount.current;
    previousNoteCount.current = newlyLinkedNotes.length;
    if (!newNotesAdded) return;

    let innerFrame = 0;
    const frame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        linkButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    });
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(innerFrame);
    };
  }, [newlyLinkedNotes]);

  return (
    <ZfStack direction="column" spacing="small" data-testid="contact-note-list-content">
      <ZfDividerTitle
        label={t('pb.contactnotes.link.title', 'Link contact notes')}
        addTopMargin={addTopMargin}
        addBottomMargin={false}
      />
      {showContactNoteRequired && (
        <ZfBanner
          data-testid="contact-note-required-alert"
          type="warning"
          dismissible={false}
          titleText={t('pb.contactnotes.required.title', 'Contact note required')}
          description={requiredAlertText}
        />
      )}
      <LinkedContactNotesGrid contactNotes={newlyLinkedNotes} onUnlink={handleUnlink} />
      {isEnvironmentResolved && (
        <ErrorBoundary
          resetKeys={[env, urlOverride]}
          fallback={
            <ZfBanner
              titleText={t('pb.contactnotes.unavailable.title')}
              description={t('pb.contactnotes.unavailable.description')}
              type="error"
            />
          }
        >
          <div ref={linkButtonRef}>
            <ContactNoteWrapperWC
              env={env as JBEnvironmentId}
              version="0.0.1"
              urlOverride={urlOverride}
              brUId={brUuid}
              size="large"
              linkedNoteIds={linkedNoteIds}
              callbackFunc={callbackFunc}
            />
          </div>
        </ErrorBoundary>
      )}
    </ZfStack>
  );
};

export { LinkContactNotesButton };
